import { useState, useEffect, useRef } from 'react';
import { ChatInput } from './chat-input';
import { ChatList } from './chat-list';
import { Card } from '@/components/shadcn-ui/card';
import { ProjectSelector } from './ProjectSelector';
import { getGuestId } from '@/utils/getGuestId';
import { appAxios } from '@/network/base/appAxios';
import { uploadToS3 } from '@/utils/s3presigned';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const APP_URL = import.meta.env.VITE_APP_API_URL;
const userId = getGuestId();

export function Chat({ protectedRoute }) {
  const [messages, setMessages] = useState([]);
  const [enablecityPermit, setenablecityPermit] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const isConnectedRef = useRef(false);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const navigate = useNavigate();
  const MAX_RECONNECT_ATTEMPTS = 3;

  const BASE_URL = APP_URL || 'http://localhost:5000';

  const getAuthToken = () => localStorage.getItem('Authorization') || '';

  useEffect(() => {
    setenablecityPermit(false);
    reconnectAttemptsRef.current = 0;

    connectSSE();
    loadChatHistory();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isConnectedRef.current) {
        reconnectAttemptsRef.current = 0;
        connectSSE();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isConnectedRef.current = false;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const connectSSE = async () => {
    if (isConnectedRef.current) return;

    try {
      const res = await fetch(`${BASE_URL}/PermitChat/v1.0/stream?userId=${userId}`, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          Accept: 'text/event-stream',
        },
      });

      if (!res.ok) {
        console.error('SSE connection failed:', res.status);
        throw new Error(`HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      isConnectedRef.current = true;
      reconnectAttemptsRef.current = 0;

      const readStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              console.log('SSE stream ended');
              break;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (!line.trim() || !line.startsWith('data: ')) continue;

              const jsonStr = line.substring(6).trim();
              if (!jsonStr) continue;

              try {
                const parsed = JSON.parse(jsonStr);
                handleSSEMessage(parsed);
              } catch (e) {
                console.error('Failed to parse SSE message:', e, jsonStr);
              }
            }
          }
        } catch (error) {
          console.error('Stream reading error:', error);
        } finally {
          isConnectedRef.current = false;
          handleReconnect();
        }
      };

      readStream();
    } catch (error) {
      console.error('SSE connection error:', error);
      isConnectedRef.current = false;
      handleReconnect();
    }
  };

  const handleReconnect = () => {
    if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);

    reconnectAttemptsRef.current += 1;

    if (reconnectAttemptsRef.current > MAX_RECONNECT_ATTEMPTS) {
      console.log('Max reconnection attempts reached. Stopping reconnection.');
      return;
    }

    reconnectTimeoutRef.current = setTimeout(connectSSE, 5000);
  };

  const handleSSEMessage = data => {
    if (data.type == 'error') {
      const message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: {
          content: 'Error occurred. Please Try Again Later.',
          model: 'AI Assistant',
          attachments: '',
          metadata: {},
        },
      };

      setMessages(prev => {
        const newMessages = [...prev, message];
        setIsLoading(false);
        return newMessages;
      });
    }
    if (data.type === 'ai_word' && data.word) {
      setStreamingMessage(prev => prev + data.word + ' ');
      return;
    }
    if (data?.type === 'ai_response' && data.data) {
      setStreamingMessage('');
      const message = {
        id: data.data._id || Date.now().toString(),
        role: 'assistant',
        content: {
          content: data.data.message,
          model: 'AI Assistant',
          attachments: '',
          metadata: data.data.metadata || {},
        },
      };

      setMessages(prev => {
        const newMessages = [...prev, message];

        // If the upload is asked we will add static message in ai end in UI for signup 
        if (data.data.metadata?.file_required == true) {
          setenablecityPermit(true);
          const newmsg = [
            ...newMessages,
            {
              id: Date.now().toString(),
              role: 'system',
              content: {
                content: 'To upload files, please sign up for an account.',
              },
            },
            {
              id: 'submit-btn-' + Date.now(),
              role: 'system',
              type: 'submit_button',
              content: {
                content: 'Click to submit your project to the city.',
              },
              metadata: data.data.metadata || {},
            }]
          setMessages(newmsg)
          return newmsg;
        }

        if (data.data.done && !prev.some(m => m.type === 'submit_button')) {
          setenablecityPermit(true);
          newMessages.push({
            id: 'submit-btn-' + Date.now(),
            role: 'system',
            type: 'submit_button',
            content: {
              content: 'Click to submit your project to the city.',
            },
            metadata: data.data.metadata || {},
          });
        }

        return newMessages;
      });
      if (data?.data) {
        setIsLoading(false);
      }
    }
  };

  const loadChatHistory = async () => {
    try {
      const res = await appAxios.get(`/PermitChat/v1.0/history`, {
        params: {
          limit: 50,
          offset: 0,
          userId,
        },
      });

      const result = res.data;

      if (result.status === 'Success' && Array.isArray(result.data.messages)) {
        const formatted = result.data.messages.map(msg => ({
          id: msg._id,
          role: msg.sender === 'user' ? 'user' : 'assistant',
          done: msg.done || false,
          content:
            msg.sender === 'user'
              ? { content: msg.message, attachments: msg.attachments || [] }
              : {
                content: msg.message,
                model: 'AI Assistant',
                attachments: '',
                metadata: msg.metadata || {},
              },
        }));

        const found = formatted.find(m => m.role === 'assistant' && m.done == true);

        if (found) {
          const lastAssistant = [...formatted].reverse().find(m => m.role === 'assistant' && m.content?.metadata);
          const lastMetadata = lastAssistant?.content?.metadata || {};

          formatted.push({
            id: 'submit-btn-' + Date.now(),
            role: 'system',
            type: 'submit_button',
            content: {
              content: 'Click to submit your project to the city.',
            },
            metadata: lastMetadata,
          });

          setenablecityPermit(true);
        }

        setMessages(formatted);
      }
    } catch (error) {
      console.error('Error loading chat:', error);
    }
  };

  const handleSendMessage = async content => {
    if (!content?.content?.trim()) return;

    const displayContent = {
      content: content.content,
      attachments: content.attachments && content.attachments.length > 0
        ? content.attachments.map(att => ({
          name: att.name,
          key: '',
          fileUrl: '',
          type: att.type,
          size: att.size,
        }))
        : []
    };

    setMessages(prev => [...prev, { id: Date.now().toString(), role: 'user', content: displayContent }]);
    setIsLoading(true);

    try {
      const payload = {
        message: { content: content.content },
        userId,
      };

      if (content.attachments && content.attachments.length > 0) {
        try {
          const uploadPromises = content.attachments.map(async att => {
            const { data, error } = await uploadToS3(att.file, 'chat-attachments');

            if (error) {
              console.error('Failed to upload file:', att.name, error);
              throw new Error(`Failed to upload ${att.name}`);
            }

            return {
              name: att.name,
              key: data.key,
              fileUrl: data.fileUrl,
              type: att.type,
              size: att.size,
            };
          });

          const uploadedFiles = await Promise.all(uploadPromises);
          payload.attachments = uploadedFiles;

          setMessages(prev => prev.map((msg, idx) =>
            idx === prev.length - 1 && msg.role === 'user'
              ? { ...msg, content: { ...msg.content, attachments: uploadedFiles } }
              : msg
          ));
        } catch (uploadError) {
          console.error('Upload error:', uploadError);
          toast.error('Failed to upload files. Please try again.');
          setIsLoading(false);
          return;
        }
      }

      const res = await appAxios.post(`/PermitChat/v1.0/message`, payload);

      const result = res.data;

      if (!isConnectedRef.current && result.data?.aiMessage) {
        setMessages(prev => {
          const newMessages = [
            ...prev,
            {
              id: result.data.aiMessage._id,
              role: 'assistant',
              content: {
                content: result.data.aiMessage.message,
                model: 'AI Assistant',
                attachments: '',
                metadata: result.data.aiMessage.metadata || {},
              },
            },
          ];

          if (result.data.aiMessage.done && !prev.some(m => m.type === 'submit_button')) {
            setenablecityPermit(true);
            newMessages.push({
              id: 'submit-btn-' + Date.now(),
              role: 'system',
              type: 'submit_button',
              content: {
                content: 'Click to submit your project to the city.',
              },
              metadata: result.data.aiMessage.metadata || {},
            });
          }

          return newMessages;
        });

        if (result?.data) {
          setIsLoading(false);
        }
      }
    } catch (err) {
      setMessages(prev => [
        ...prev,
        {
          id: Date.now().toString(),
          role: 'assistant',
          content: {
            content: 'Error occurred. Try again.',
            model: 'System',
          },
        },
      ]);
      setIsLoading(false);
    }
  };
  return (
    <div className="flex flex-col items-center justify-center my-16 h-full">
      {messages.length === 0 && !isLoading &&
        <p className="text-center text-[22px] sm:text-[28px] font-light text-white leading-tight mb-6 line-height-[50px] w-full">
          Submit Your Permit Application in Just 2–3 Minutes
        </p>
      }
      <Card className="flex flex-col bg-neutral-900 p-4 md:p-6 rounded-xl w-full h-full font-baloo-2 chat-box">
        {/* Avatar + Title (NOT scrollable) */}
        {messages.length === 0 && !isLoading && (
          <div className="max-w-2xl mx-auto flex flex-col items-center shrink-0">
            <div className="mb-4">
              <img src="/kelly.png" alt="Kelly Avatar" className="w-32 h-32 sm:w-40 sm:h-40 object-cover" />
            </div>
            <h2 className="text-xl sm:text-2xl font-medium text-white">Hey, I'm Kelly!</h2>
            <p className="text-base sm:text-lg text-gray-300">What type of project would you like to submit today?</p>
          </div>
        )}

        {messages.length === 0 && !isLoading ? (
          <div className="">
            {/* <ProjectSelector onSelect={option => handleSendMessage({ content: option })} /> */}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto no-scrollbar mt-4 min-h-[300px] h-full">
            <ChatList
              messages={messages}
              isLoading={isLoading}
              streamingMessage={streamingMessage}
              protectedRoute={protectedRoute}
              showAcknowledgmentModal={false}
              AcknowledgmentModalContent={[
                "I agree to the Terms and Conditions.",
                "I understand permits are issued by the City.",
                "I confirm the information provided is accurate."
              ]}
            />
          </div>
        )}
        {/* Static Input Bar */}
        {!enablecityPermit && (
          <div className={`pt-3 ${messages.length > 0 ? 'border-t border-neutral-800' : ''} mt-2 shrink-0`}>
            <ChatInput onSendMessage={handleSendMessage} disabled={isLoading} showActions={false} />
          </div>
        )}

        <div className="text-center text-xs text-white font-thin">
          By messaging Zermit AI, you agree to our <span className="underline cursor-pointer" onClick={() => navigate('/terms')}>Terms</span> and have read
          our <span className="underline cursor-pointer" onClick={() => navigate('/terms')}>Privacy Policy. </span>
        </div>
      </Card>
    </div>
  );
}
